import React from 'react'
import { Button, Icon } from 'semantic-ui-react'
import styled from 'styled-components'

import PageHeader from '../../components/pageHeader/pageHeader'
import PagedTable from '../../components/dataTable/pagedTable'
import RemoteValue from '../../components/remoteValues/remoteValue'
import { Company, RecurringCharge } from '../../types'
import { currencyFormat } from '../../utils'
import RecurringChargeSidePanel from './RecurringChargeSidePanel'

const PageContent = styled.div`
  margin: 0px 50px 50px 50px;
`

const RecurringChargesPage = () => {
  document.title = 'Recurring Charges'

  const [panelState, setPanelState] = React.useState<{
    open: boolean
    recurringChargeId?: string | null
  }>({ open: false, recurringChargeId: null })

  const [tableRefreshKey, setTableRefreshKey] = React.useState(0)

  const handleAddRecurringCharge = () => {
    setPanelState({ open: true, recurringChargeId: null })
  }

  const handleEditRecurringCharge = (charge: RecurringCharge) => {
    setPanelState({ open: true, recurringChargeId: charge._id })
  }

  const handleClosePanel = () => {
    setPanelState({ open: false, recurringChargeId: null })
  }

  const handleReloadTable = () => {
    setTableRefreshKey((prev) => prev + 1)
  }

  const handleSaved = () => {
    handleReloadTable()
    handleClosePanel()
  }

  const handleDeleted = () => {
    handleReloadTable()
    handleClosePanel()
  }

  return (
    <React.Fragment>
      <PageHeader
        breadcrumb={[
          { text: 'Dashboard', link: '/' },
          { text: 'Recurring Charges', link: '/recurring-charges' },
        ]}
        title="Recurring Charges"
        sub="Manage automated billing charges for your customers."
        actions={
          <Button basic onClick={handleAddRecurringCharge}>
            <Icon name="add" /> Add Recurring Charge
          </Button>
        }
      />

      <PageContent>
        <PagedTable<RecurringCharge>
          key={tableRefreshKey}
          collection="billing-recurring-charges"
          defaultSort="lastUpdateOn"
          onRowClick={handleEditRecurringCharge}
          columns={[
            {
              title: 'Customer',
              field: 'customerId',
              render: (customerId: string | null | undefined) =>
                customerId ? (
                  <RemoteValue
                    collection="companies"
                    id={customerId}
                    key={customerId}
                    predicate={(company: Company) => company.name || '—'}
                    showLoading
                  />
                ) : (
                  '—'
                ),
            },
            {
              title: 'Description',
              field: 'description',
              width: 320,
            },
            {
              title: 'Amount',
              field: 'amount',
              width: 140,
              align: 'right',
              render: (value: number) => currencyFormat(Number(value) || 0),
            },
            {
              title: 'Status',
              field: 'active',
              width: 120,
              align: 'center',
              render: (active: boolean) => (active ? 'Active' : 'Inactive'),
            },
          ]}
        />

        <RecurringChargeSidePanel
          open={panelState.open}
          recurringChargeId={panelState.recurringChargeId ?? undefined}
          onClose={handleClosePanel}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      </PageContent>
    </React.Fragment>
  )
}

export default RecurringChargesPage
